import React, { useState, useEffect } from 'react'
import { details } from '../Projects/Details';
import { useParams, Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from "framer-motion";
import { IoClose } from "react-icons/io5";
import { MdNavigateNext, MdNavigateBefore } from "react-icons/md";
import './SingleProjectPage.css'

const typeLabels = {
    restobar: 'Restaurant & Bar',
    residential: 'Residential',
    commercial: 'Commercial Office',
    hospitality: 'Hospitality',
    food_court: 'Food Court',
}

const SingleProjectPage = () => {
    const { id } = useParams()
    const location = useLocation()
    const fromFilter = location.state?.fromFilter ?? null
    const projectDetails = details.find(p => p.id == id) || {}  // eslint-disable-line

    const [isOpen, setIsOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const images = projectDetails.images || []
    const showNavigation = images.length > 1;

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!isOpen) return;
            if (e.key === 'Escape') setIsOpen(false);
            if (e.key === 'ArrowRight') handleNext();
            if (e.key === 'ArrowLeft') handlePrevious();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, currentIndex]);  // eslint-disable-line

    const handleImageClick = (index) => {
        setCurrentIndex(index);
        setIsOpen(true);
    };

    const handleNext = () => {
        if (!showNavigation) return;
        setCurrentIndex((prev) => (prev + 1) % images.length);
    };

    const handlePrevious = () => {
        if (!showNavigation) return;
        setCurrentIndex((prev) => prev === 0 ? images.length - 1 : prev - 1);
    };

    const handleDragEnd = (_, info) => {
        if (info.offset.x < -60) handleNext();
        else if (info.offset.x > 60) handlePrevious();
    };

    return (
        <div className='single-project-page'>

            {/* Page header */}
            <div className='project-header-bar'>
                <div className='project-header-inner'>
                    <motion.div
                        className='back-nav'
                        initial={{ opacity: 0, x: -16 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4 }}
                    >
                        <Link
                            to="/projects"
                            state={fromFilter ? { initialFilter: fromFilter } : undefined}
                            className='back-button'
                        >
                            {fromFilter ? '← Back' : '← All Projects'}
                        </Link>
                    </motion.div>
                </div>
            </div>

            <motion.div
                className='project-hero-info'
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.1 }}
            >
                <span className='project-type-tag'>{typeLabels[projectDetails.type] || projectDetails.type}</span>
                <h1 className='project-name'>{projectDetails.projectName}</h1>
                <p className='project-meta'>{projectDetails.location} · {images.length} {images.length === 1 ? 'image' : 'images'}</p>
                <p className='project-description'>{projectDetails.details}</p>
            </motion.div>

            {/* Gallery grid */}
            <motion.div
                className='gallery-grid'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
            >
                {images.map((path, index) => (
                    <motion.div
                        key={index}
                        className={`gallery-item ${index === 0 ? 'gallery-item--featured' : ''}`}
                        whileHover={{ scale: 1.01 }}
                        transition={{ duration: 0.3 }}
                        onClick={() => handleImageClick(index)}
                    >
                        <img src={path} alt={`${projectDetails.projectName} ${index + 1}`} className="gallery-image" />
                    </motion.div>
                ))}
            </motion.div>

            {/* Lightbox / Swipeable slideshow */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="slideshow-overlay"
                        onClick={() => setIsOpen(false)}
                    >
                        <motion.div
                            className="slideshow-container"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button className="close-button" onClick={() => setIsOpen(false)}>
                                <IoClose size={22} />
                            </button>

                            {/* Swipeable image */}
                            <motion.div
                                className="slide-image-wrap"
                                drag="x"
                                dragConstraints={{ left: 0, right: 0 }}
                                dragElastic={0.2}
                                onDragEnd={handleDragEnd}
                            >
                                <AnimatePresence mode="wait">
                                    <motion.img
                                        key={currentIndex}
                                        src={images[currentIndex]}
                                        alt={`Slide ${currentIndex + 1}`}
                                        className="slide-image"
                                        initial={{ opacity: 0, scale: 0.97 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.97 }}
                                        transition={{ duration: 0.25 }}
                                        draggable={false}
                                    />
                                </AnimatePresence>
                            </motion.div>

                            {/* Desktop prev/next buttons */}
                            {showNavigation && (
                                <>
                                    <button className="nav-button prev" onClick={handlePrevious}>
                                        <MdNavigateBefore size={36} />
                                    </button>
                                    <button className="nav-button next" onClick={handleNext}>
                                        <MdNavigateNext size={36} />
                                    </button>
                                </>
                            )}

                            {/* Dot indicators */}
                            {showNavigation && (
                                <div className="slide-dots">
                                    {images.map((_, i) => (
                                        <button
                                            key={i}
                                            className={`slide-dot${i === currentIndex ? ' active' : ''}`}
                                            onClick={() => setCurrentIndex(i)}
                                            aria-label={`Go to image ${i + 1}`}
                                        />
                                    ))}
                                </div>
                            )}

                            {/* Counter */}
                            <div className="slide-counter">
                                {currentIndex + 1} / {images.length}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

export default SingleProjectPage
